import * as Device from "expo-device";
import api from "./api";
import { trackRoleSwitched } from "./analytics-events";
import { appendFileToFormData } from "./form-data";
import { User } from "./types/user";

export interface AuthResponse {
  user: User;
  token: string;
}

export interface UserResponse {
  user: User;
  message: string;
}

export interface RegisterData {
  first_name: string;
  middle_name?: string;
  last_name: string;
  email: string;
  password: string;
  password_confirmation: string;
  phone: string;
  role: "customer" | "business_customer" | "driver";
  address?: string;
  company_name?: string;
  company_registration_number?: string;
  vat_number?: string;
  referral_code?: string;
  origin_city?: string;
  origin_latitude?: number;
  origin_longitude?: number;
}

export interface LoginData {
  email: string;
  password: string;
  fcm_token?: string;
  device_name?: string;
}

export function formatLoginDeviceName(
  modelName: string | null,
  osName: string | null,
  osVersion: string | null,
): string | undefined {
  const os = [osName, osVersion].filter(Boolean).join(" ");
  const label = [modelName, os].filter(Boolean).join(" · ");

  return label || undefined;
}

class AuthService {
  async register(data: RegisterData): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>("/register", data);
    return response.data;
  }

  async login(data: LoginData): Promise<AuthResponse> {
    const deviceName = formatLoginDeviceName(
      Device.modelName,
      Device.osName,
      Device.osVersion,
    );

    const response = await api.post<AuthResponse>("/login", {
      ...data,
      ...(deviceName ? { device_name: deviceName } : {}),
    });
    return response.data;
  }

  async logout(): Promise<void> {
    try {
      await api.post("/logout");
    } catch (error) {
      console.error("Logout error:", error);
    }
  }

  async forgotPassword(email: string): Promise<void> {
    await api.post("/forgot-password", { email });
  }

  async resetPassword(data: {
    email: string;
    token: string;
    password: string;
    password_confirmation: string;
  }): Promise<void> {
    await api.post("/reset-password", data);
  }

  async sendVerificationCode(): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>("/email/verification-code");
    return response.data;
  }

  async verifyEmail(code: string): Promise<User> {
    const response = await api.post<User>("/email/verify", { code });
    return response.data;
  }

  async getUser(): Promise<User> {
    const response = await api.get<User>("/me");
    return response.data;
  }

  async deviceToken(data: { fcm_token: string }): Promise<{ message: string, fcm_token: string }> {
    const response = await api.post<{ message: string, fcm_token: string }>("/device-token", data);
    return response.data;
  }

  async updateProfile(data: Partial<User>): Promise<UserResponse> {
    const response = await api.put<UserResponse>("/user", data);
    return response.data;
  }

  async updateEmailPreferences(
    data: Partial<Pick<User, "job_offer_emails" | "account_update_emails">>,
  ): Promise<UserResponse> {
    const response = await api.put<UserResponse>("/user/email-preferences", data);
    return response.data;
  }

  async changePassword(data: {
    current_password: string;
    password: string;
    password_confirmation: string;
  }): Promise<void> {
    await api.put("/change-password", data);
  }

  async updateAvatar(uri: string, type: string, name: string): Promise<{ message: string, avatar: string }> {
    const formData = new FormData();
    await appendFileToFormData(formData, "avatar", { uri, type, name });

    const response = await api.post("/user/avatar", formData);
    return response.data;
  }

  async deleteAccount(password: string): Promise<void> {
    const response = await api.delete("/user", { data: { password } });
    return response.data;
  }

  async switchRole(): Promise<UserResponse> {
    const response = await api.post<UserResponse>("/user/switch-role");
    trackRoleSwitched(response.data.user.role);
    return response.data;
  }

  async getAccountMode(): Promise<{
    account_type: string;
    active_mode: string;
    default_job_origin: string;
    default_pricing_engine: string;
  }> {
    const response = await api.get("/account/mode");
    return response.data.data;
  }

  async updateAccountMode(active_mode: "CUSTOMER" | "OPERATOR" | "ADMIN"): Promise<{
    account_type: string;
    active_mode: string;
    default_job_origin: string;
    default_pricing_engine: string;
    note?: string | null;
  }> {
    const response = await api.put("/account/mode", { active_mode });
    return response.data.data;
  }
}

export default new AuthService();
